import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  Input,
  filledInputClasses,
  Divider,
  InputLabelProps,
} from "@mui/material";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { ReactComponent as LoadIcon } from "../../../images/loadExampleButton.svg";
import { useNavigate } from "react-router-dom";
import CommonAccordion from "../../custom-components/CommonAccordion";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import {
  fetchJiraIssuesApiCall,
  freeFlowBugReportEnhance,
  getJiraConfig,
  scrutinize,
  fetchSpecificIssues,
} from "../../../services/webServices";
import { validateUserInput } from "../../../services/webServices";
import {
  LoadingModalContext,
  MessagePopupContext,
} from "../../main-pages/ServicePage";
import { BugReportDataContext } from "../../main-pages/BugReportPage";
import { ReactComponent as ImportIcon } from "../../../images/importIcon.svg";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import SessionExpired from "../../popup-components/SessionExpiredPopup";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";
import MessagePopup from "../../popup-components/MessagePopup";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const BugReportComponent = () => {
  const [accordionOpen, setAccordionOpen] = useState(true);
  // Free flow bug report variable
  const [bugDetails, setBugDetails] = useState("");
  // State variables for each form field of bug report
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const { setMessagePopup, setMessage } = useContext(MessagePopupContext);
  const [environmentDetails, setEnvironmentDetails] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [expectedResult, setExpectedResult] = useState("");
  const [actualResult, setActualResult] = useState("");
  const [additionalInformation, setAdditionalInforamtion] = useState("");
  const [impact, setImpact] = useState("");
  // const [explorationDepth, setExplorationDepth] = useState("");
  const [textFieldError, setTextFieldError] = useState("");
  // State variable for enhance bug report button status (enabled or not)
  const [enhancedBugReportButtonEnabled, setEnhancedBugReportButtonEnabled] =
    useState(false);
  // Getting setLoading method using UseContext to set the loading modal state
  const { setLoadingState } = useContext(LoadingModalContext);
  // Getting setBugReportData and setEnhancedBugReportData to change the state variable in BugReportPage
  const { setBugReportData, setEnhancedBugReportData } =
    useContext(BugReportDataContext);

  const [projectId, setProjectId] = useState("");
  const [jiraIssues, setJiraIssues] = useState([]);

  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);
  const [openProjectPage, setOpenProjectPage] = useState(false);
  const [projectListLength, setProjectListLength] = useState(0);
  // Use navigate hook for routing
  const navigate = useNavigate();

  const examples = {
    LoadExample: {
      title: "Incorrect Policy Renewal Date Display",
      description:
        "The policy renewal date displayed on the user's account is incorrect, showing past dates for policies that are still active.",
      environmentDetails:
        "Application Version: 3.5.1\nBrowser: Firefox 91.0\nOS: macOS Catalina\nDevice: Desktop",
      stepsToReproduce:
        "Log into the insurance portal.\nNavigate to the 'My Policies' section.\nReview the renewal dates for active policies.",
      expectedResult:
        "The renewal date should accurately reflect the next renewal date for active policies.",
      actualResult:
        "The renewal date is displayed as a past date, even for policies that are still active.",
      additionalInfo:
        "Users have reported discrepancies in renewal dates across different types of policies.",
      impact:
        "Medium - Users are misinformed about their policy status, potentially leading to missed renewals and lapsed coverage.",
      explorationDepth: "Simple",
    },
  };

  const selectLoadExample = (event) => {
    event.stopPropagation();
    setTitle(examples["LoadExample"]["title"]);
    setDescription(examples["LoadExample"]["description"]);
    setEnvironmentDetails(examples["LoadExample"]["environmentDetails"]);
    setStepsToReproduce(examples["LoadExample"]["stepsToReproduce"]);
    setExpectedResult(examples["LoadExample"]["expectedResult"]);
    setActualResult(examples["LoadExample"]["actualResult"]);
    setAdditionalInforamtion(examples["LoadExample"]["additionalInfo"]);
    setImpact(examples["LoadExample"]["impact"]);
    // setExplorationDepth(examples["LoadExample"]["explorationDepth"]);
    setTextFieldError(false);
  };

  useEffect(() => {
    setBugReportData({});
    setEnhancedBugReportData({});
  }, []);

  useEffect(() => {
    // Set to true only if all fields are filled (trimmed)
    const isFormComplete =
      title.trim() &&
      description.trim() &&
      // environmentDetails.trim() &&
      stepsToReproduce.trim() &&
      expectedResult.trim() &&
      actualResult.trim();

    if (enhancedBugReportButtonEnabled !== isFormComplete) {
      setEnhancedBugReportButtonEnabled(isFormComplete);
    }
  }, [
    title,
    description,
    expectedResult,
    actualResult,
    // environmentDetails,
    stepsToReproduce,
  ]);

  const commonTextFieldStyle = {
    width: "100%",
    mt: 1,
    mb: 2,
  };

  const largerTextFieldStyle = {
    ...commonTextFieldStyle,
    minRows: 8,
  };

  const handleAccordionToggle = () => {
    setAccordionOpen((prev) => !prev);
  };

  const handleEnhanceDescription = async () => {
    let progressHandler;

    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 20000,
      });

      progressHandler.start("Validating the input");

      const reqData = {
        bugDetails: bugDetails,
        promptType: "bugDetails",
      };

      const validationResponse = await validateUserInput(reqData);
      const validationResult = validationResponse?.data?.Result?.toLowerCase();

      if (validationResult !== "clean") {
        const errorMsg =
          validationResponse?.data?.message ||
          validationResponse?.data?.Reason ||
          "Inappropriate bug details detected. Please revise and try again.";

        setMessage(errorMsg);
        setMessagePopup(true);
        setLoadingState({ open: false, message: "", progress: null });
        return;
      }

      progressHandler.updateMessage("Enhancing Bug Description");
      const enhancementResponse = await freeFlowBugReportEnhance({
        bugDetails: bugDetails,
      });
      await progressHandler.finishAndClose();

      navigate("/dashboard/bugreportenhancer/output", {
        state: {
          bugReportData: bugDetails,
          enhancedBugReport: enhancementResponse.data,
        },
      });
    } catch (error) {
      console.error("Error enhancing bug description:", error);

      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.Reason ||
        "An error occurred.";

      setMessage(errorMsg);
      setMessagePopup(true);

      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      progressHandler.stop();
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  // const handleChange = (event) => {
  //   setExplorationDepth(event.target.value);
  // };

  const handleFormSubmit = async () => {
    const bugReportData = {
      title: title,
      description: description,
      environmentDetails: environmentDetails,
      stepsToReproduce: stepsToReproduce,
      expectedResult: expectedResult,
      actualResult: actualResult,
      additionalInfo: additionalInformation,
      impact: impact,
      // explorationDepth: explorationDepth,
    };
    //setLoading(true);
    let progressHandler;
    try {
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 15000,
      });

      progressHandler.start("Enhancing Bug Description");
      const requestData = {
        bugReportData: bugReportData,
        // temperature: explorationDepth,
      };

      const enhancedReport = await scrutinize(requestData);
      await progressHandler.finishAndClose();

      navigate("/dashboard/bugreportenhancer/output", {
        state: { bugReportData, enhancedBugReport: enhancedReport.data },
      });
    } catch (error) {
      console.error("Error submitting bug report:", error);

      const errorMsg =
        error?.response?.data?.Error ||
        "Something went wrong while submitting the form.";

      setPopupMessage(errorMsg);
      setPopupOpen(true);

      if (errorMsg === "Your session has expired.") {
        setSessionExpiredValue(true);
      }

      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      progressHandler.stop();
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const handleToNavigateMultiple = async () => {
    setOpenProjectPage(true);
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 0,
        bgcolor: "#f6f3f2",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Card for the entire Bug Report */}
      <Box
        sx={{
          // width: "601px",
          height: "auto",
          boxShadow: 0,
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          bgcolor: "transparent",
          border: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          padding: 0,
          mb: 1, // Reduced from mb: 4 to mb: 1
        }}
      >
        {/* Breadcrumb Navigation */}
        <Box
          sx={{
            bgcolor: "transparent",
            marginBottom: 1,
            padding: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center" mb={0.1}>
              <AutomateIcon style={{ marginRight: 4 }} />
              {/* Informative text */}
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ mt: 0.5 }}
              >
                Generate detailed and insightful bug reports with ease
              </Typography>
            </Box>
            {/* <FullscreenToggle /> */}
          </Box>
        </Box>

        {/* Card 1: Bug Details Form */}
        <Card
          sx={{
            bgcolor: "white",
            marginBottom: 1,
            borderRadius: "16px",
            border: "1px solid rgba(0,0,0,0.12)",
          }}
        >
          <CardContent sx={{ padding: 1 }}>
            {" "}
            {/* Reduced padding */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                //border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "8px 12px",
                backgroundColor: "white",
              }}
            >
              {/* <SearchIcon sx={{ mt: "6px", mr: 1, color: "gray" }} /> */}
              {/* Scrollable Textarea Wrapper */}
              <Box
                sx={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  width: "100%",
                  scrollbarWidth: "thin", // Firefox
                  WebkitOverflowScrolling: "touch",
                  "&::-webkit-scrollbar": {
                    width: "4px", // thinner scrollbar
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#aaa",
                    borderRadius: "2px",
                    minHeight: "20px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#777",
                  },
                }}
              >
                <TextareaAutosize
                  placeholder="Enter bug details"
                  minRows={6}
                  value={bugDetails}
                  onChange={(e) => setBugDetails(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    resize: "none",
                    width: "100%",
                    fontSize: "16px",
                    fontFamily: "inherit",
                    backgroundColor: "transparent",
                    overflowX: "hidden", // prevents content from causing horizontal scroll
                  }}
                />
              </Box>
            </Box>
            <hr style={{ borderColor: "rgba(0, 0, 0, 0.12)" }} />
            <Box sx={{ display: "flex", justifyContent: "flex-end", pr: 3 }}>
              <Button
                variant="contained"
                color="primary"
                // startIcon={<GenerateButton />}
                sx={{
                  mt: 1,
                  textTransform: "none",
                  backgroundColor: "#49A3FF",
                }}
                disabled={!bugDetails.trim()}
                onClick={handleEnhanceDescription}
              >
                Enhance Bug Description
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            my: 2, // vertical spacing
          }}
        >
          <Divider sx={{ flexGrow: 1 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mx: 2, whiteSpace: "nowrap" }}
          >
            OR
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <CommonAccordion
          summary={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="subtitle1" sx={{ fontSize: "16px" }}>
                Bug details
              </Typography>
              <Button
                variant="text"
                startIcon={<LoadIcon />}
                sx={{ textTransform: "none" }}
                onClick={selectLoadExample}
              >
                Load sample text
              </Button>
            </Box>
          }
          type={"input"}
          details={
            <CardContent sx={{ padding: 0 }}>
              <Box
                sx={{
                  maxHeight: "300px",
                  overflowY: "auto",
                  pr: 1,
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-track": { backgroundColor: "#f0f0f0" },
                }}
              >
                <TextField
                  label="Title*"
                  placeholder="Provide a brief and descriptive title for the bug"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  multiline
                  sx={commonTextFieldStyle}
                />
                <TextField
                  label="Description*"
                  placeholder="Explain the issue in detail, including the relevant context"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  sx={largerTextFieldStyle}
                />
                <TextField
                  label="Environment Details"
                  placeholder="Specify the environment where the bug was found (e.g., operating system, browser, device)"
                  value={environmentDetails}
                  onChange={(e) => setEnvironmentDetails(e.target.value)}
                  margin="normal"
                  multiline
                  sx={commonTextFieldStyle}
                />
                <TextField
                  label="Steps to Reproduce*"
                  placeholder="List the step by step instructions to replicate the issue"
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  sx={largerTextFieldStyle}
                />
                <TextField
                  label="Expected Result*"
                  placeholder="Describe the desired behaviour or outcome that should occur when following the reproduction steps"
                  value={expectedResult}
                  onChange={(e) => setExpectedResult(e.target.value)}
                  margin="normal"
                  multiline
                  sx={commonTextFieldStyle}
                />
                <TextField
                  label="Actual Result*"
                  placeholder="Describe the actual behaviour or outcome that occurs when following the reproduction steps"
                  value={actualResult}
                  onChange={(e) => setActualResult(e.target.value)}
                  multiline
                  sx={commonTextFieldStyle}
                />
                <TextField
                  label="Additional Information"
                  placeholder="Include any other relevant details such as error messages,logs"
                  value={additionalInformation}
                  onChange={(e) => setAdditionalInforamtion(e.target.value)}
                  margin="normal"
                  multiline
                  sx={commonTextFieldStyle}
                />
                <TextField
                  label="Impact"
                  placeholder="Include the severity or impact of the bug on the system or users"
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  margin="normal"
                  multiline
                  sx={commonTextFieldStyle}
                />
              </Box>
            </CardContent>
          }
          buttons={[
            {
              label: "Add More",
              variant: "text",
              startIcon: <AddIcon />,
              disabled: true,
            },
            {
              label: "Import",
              variant: "text",
              startIcon: <ImportIcon />,
              onClick: handleToNavigateMultiple,
            },
            {
              label: "Enhance Bug Description",
              variant: "contained",
              sx: { float: "right" },
              disabled: !enhancedBugReportButtonEnabled,
              onClick: handleFormSubmit,
            },
          ]}
          sx={{
            "&:hover, &:focus": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "16px",
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
            },
          }}
        />
      </Box>
      {openProjectPage && (
        <ALMConnectionPage
          issueType={"Bugs"}
          setPageCloser={setOpenProjectPage}
        />
      )}
      {sessionExpiredValue && <SessionExpired />}
    </Box>
  );
};

export default BugReportComponent;
